import { Node, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import HelptapEditor from '../../helpers';
import { findPreviewBreakpoint } from '../../node-menu/layout-settings/preview-breakpoint';

import { findCells, measureFreeSpace, measureRoomAround, measureWidth } from './gap-measure';

// A drag shows on the two cells only; the value changes once, when the drag ends.

function readSide(layoutNode, index, breakpoint, cell) {
  return { size: HelptapEditor.layout.readGapItemSize(layoutNode, index, breakpoint), width: measureWidth(cell) };
}

function showCellStyle(session, side, changes) {

  const cell = session.cells[side];

  cell.style.cssText = session.savedStyles[side];

  if (changes === null) {
    return;
  }

  const style = HelptapEditor.layout.previewCellStyle(session.layoutNode, session.indexes[side], session.breakpoint, changes);
  const keys = Object.keys(style);

  for (let index = 0; index < keys.length; index++) {
    cell.style.setProperty(keys[index], String(style[keys[index]]));
  }

}

function measureGapX(session) {
  return (session.cells.left.getBoundingClientRect().right + session.cells.right.getBoundingClientRect().left) / 2;
}

function showPlan(session, delta) {

  session.plan = HelptapEditor.layout.planGapResize(session.start, delta);

  showCellStyle(session, 'left', session.plan.left);
  showCellStyle(session, 'right', session.plan.right);

}

const CORRECTION_PASSES = 3;

// When the gap doesn't follow the item one to one, as in a centred row, the growth is corrected
// until the gap is under the pointer. It restarts from the growth used, so dragging past the
// largest size doesn't build up a lag on the way back.
function followPointer(session, delta) {

  for (let pass = 0; pass < CORRECTION_PASSES && session.plan.applied !== null; pass++) {

    const applied = session.plan.applied;
    const moved = measureGapX(session) - session.startGapX;

    if (Math.abs(delta - moved) < 1) {
      return;
    }

    // The gap moves in proportion to the growth, so one pass usually lands it.
    if (Math.abs(applied) >= 1 && Math.abs(moved) >= 1) {
      showPlan(session, applied * delta / moved);
    } else {
      showPlan(session, applied + delta - moved);
    }

  }

}

function restoreCells(session) {
  session.cells.left.style.cssText = session.savedStyles.left;
  session.cells.right.style.cssText = session.savedStyles.right;
}

// gap: { leftIndex, rightIndex } as measured on screen.
export function beginGapDrag(editor, element, container, gap) {

  const path = ReactEditor.findPath(editor, element);
  const layoutNode = Node.get(editor, path);
  const breakpoint = findPreviewBreakpoint(container);
  const cells = findCells(container);
  const left = cells[gap.leftIndex];
  const right = cells[gap.rightIndex];

  Transforms.deselect(editor);

  return {
    editor,
    path,
    layoutNode,
    breakpoint,
    indexes: { left: gap.leftIndex, right: gap.rightIndex },
    cells: { left, right },
    savedStyles: { left: left.style.cssText, right: right.style.cssText },
    start: {
      left: readSide(layoutNode, gap.leftIndex, breakpoint, left),
      right: readSide(layoutNode, gap.rightIndex, breakpoint, right),
      containerWidth: container.clientWidth,
      free: measureFreeSpace(container) + measureRoomAround(container)
    },
    startGapX: (left.getBoundingClientRect().right + right.getBoundingClientRect().left) / 2,
    plan: null
  };

}

// delta: px the handle moved right since the drag began. Returns the label for the tooltip.
export function moveGapDrag(session, delta) {

  showPlan(session, delta);
  followPointer(session, delta);

  return session.plan.label;

}

export function endGapDrag(session) {

  const changes = {};

  restoreCells(session);

  if (session.plan === null) {
    return;
  }

  if (session.plan.left !== null) {
    changes[session.indexes.left] = session.plan.left;
  }

  if (session.plan.right !== null) {
    changes[session.indexes.right] = session.plan.right;
  }

  HelptapEditor.layout.resizeGap(session.editor, session.path, session.breakpoint, changes);

}
